import type React from "react";
import { Center, Group, Loader, Stack, Text } from "@mantine/core";
import FieldList from "@components/FieldList";
import { Alert } from "@xplatform/design-system";
import type { RepairSearchResponse } from "@models/repairInformation";
import AnswerPanel from "./AnswerPanel";
import RepairHitCard from "./RepairHitCard";
import { MODULE_LABELS } from "./facts";
import type { RepairSearchState } from "./useRepairInformation";

const STATUS_MESSAGE: Partial<Record<RepairSearchResponse["status"], { color: string; title: string; text: string }>> = {
  no_match: {
    color: "gray",
    title: "No match",
    text: "Nothing in this vehicle's repair information matches the question.",
  },
  no_coverage: {
    color: "yellow",
    title: "Not published",
    text: "This vehicle has no repair information published in the selected language.",
  },
  degraded: {
    color: "orange",
    title: "Partial result",
    text: "A step fell back while answering; the result may be incomplete.",
  },
};

/** The answer, sourced facts and (optionally) trace of a repair-information search. */
const RepairInformationResults: React.FC<{ state: RepairSearchState }> = ({
  state: { loading, error, result },
}) => {
  if (loading) {
    return (
      <Center mih={160}>
        <Loader />
      </Center>
    );
  }
  if (error) {
    return (
      <Alert variant="light" color="red" title="Search failed">
        {error}
      </Alert>
    );
  }
  if (!result) return null;

  const banner = STATUS_MESSAGE[result.status];
  const cited = new Set(result.citations ?? []);

  return (
    <Stack gap="md">
      {banner && (
        <Alert variant="light" color={banner.color} title={banner.title}>
          {banner.text}
          {result.degraded.length > 0 && ` (${result.degraded.join(", ")})`}
        </Alert>
      )}
      {result.answer && (
        <AnswerPanel answer={result.answer} />
      )}
      {result.hits.length > 0 && (
        <Group justify="space-between" gap="sm" px={4}>
          <Text size="sm" fw={500}>
            {result.hits.length} result{result.hits.length === 1 ? "" : "s"}
          </Text>
          <FieldList
            fields={result.moduleResults
              .filter((m) => m.hits > 0)
              .map((m) => [MODULE_LABELS[m.module] ?? m.module, String(m.hits)] as const)}
          />
        </Group>
      )}
      {result.hits.map((hit, index) => (
        <RepairHitCard key={hit.key} hit={hit} rank={index + 1} cited={cited.has(hit.key)} />
      ))}
    </Stack>
  );
};

export default RepairInformationResults;
